import { faCheck } from "@fortawesome/pro-solid-svg-icons";
import { Box, Collapse, LinearProgress, Stack, Typography } from "@mui/material";
import { formatBytes } from "@shuttlerock/data-display";
import { FaIcon } from "@shuttlerock/mui-components";
import { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useUploadState } from "../../contexts/UploadContext";
import i18n, { defaultNS } from "../../i18n";

export type ProgressBarProps = {
  scope: string;
};

type UseUploadProgress = {
  totalFiles: number;
  totalBytes: string;
  uploadedBytes: string;
  totalProgress: number;
};

/** @internal */
export const useUploadProgress = (scope: string): UseUploadProgress =>
  useUploadState({
    selector: (state) => {
      const scoped = state[scope];
      const fileCount = Object.keys(scoped.files || {}).length;
      const [bytesTotal, bytesUploaded] = Object.values(scoped.files || {}).reduce(
        (acc, curr) => {
          const [total, uploaded] = acc;
          return [
            total + (curr.progress?.bytesTotal || 0),
            uploaded + (curr.progress?.bytesUploaded || 0),
          ];
        },
        [0, 0],
      );

      return {
        totalFiles: fileCount,
        totalBytes: formatBytes(bytesTotal),
        uploadedBytes: formatBytes(bytesUploaded),
        totalProgress: scoped.totalProgress,
      };
    },
  });

export const ProgressBar = ({ scope }: ProgressBarProps): ReactElement | null => {
  const { t } = useTranslation(defaultNS, { i18n });
  const { totalProgress, totalFiles, totalBytes, uploadedBytes } = useUploadProgress(scope);
  const isComplete = totalProgress === 100;

  return totalProgress > 0 ? (
    <Collapse in>
      <Box data-testid={`Uploading-ProgressBarContainer-${scope}`} minHeight={28}>
        <Stack gap={0.5}>
          <LinearProgress
            variant="determinate"
            value={totalProgress}
            color={isComplete ? "success" : "primary"}
          />
          <Typography
            variant="helperText"
            color="grey.A500"
            component={Stack}
            direction="row"
            justifyContent="space-between"
          >
            <Stack direction="row" gap={1}>
              {isComplete ? (
                <Stack alignItems="center" gap={0.5} direction="row">
                  <FaIcon icon={faCheck} color="success" />
                  <span>{t("progress:complete")}</span>
                </Stack>
              ) : (
                <span>{t("progress:percent", { percent: totalProgress })}</span>
              )}
              <Stack direction="row" gap={1} sx={{ display: { xs: "none", sm: "flex" } }}>
                <span>·</span>
                <span>{t("progress:fileCount", { count: totalFiles })}</span>
              </Stack>
            </Stack>
            <span>
              {t("progress:transferred", {
                uploaded: uploadedBytes,
                total: totalBytes,
              })}
            </span>
          </Typography>
        </Stack>
      </Box>
    </Collapse>
  ) : null;
};
